/**
 * API Configuration
 * Base URL and common fetch utilities for FreighAI API
 */

// Base API URL - in development, use relative URL to go through Vite proxy
// In production, use the environment variable
export const API_BASE_URL = import.meta.env.VITE_API_URL || ''

/**
 * API Error class for standardized error handling
 */
export class ApiError extends Error {
  status: number
  data?: unknown

  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

/**
 * Generic fetch wrapper with error handling
 */
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`

  // Let the browser set Content-Type (with boundary) for FormData uploads.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData

  const defaultHeaders: HeadersInit = {
    ...(!isFormData && { 'Content-Type': 'application/json' }),
    // Add tunnel-specific headers to skip interstitial pages
    ...(API_BASE_URL.includes('ngrok') && { 'ngrok-skip-browser-warning': 'true' }),
    ...(API_BASE_URL.includes('zrok') && { 'skip_zrok_interstitial': 'TRUE' }),
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  }

  const response = await fetch(url, config)

  // Parse response body
  const data = await response.json().catch(() => null)

  if (!response.ok) {
    const message = data?.message || `HTTP error ${response.status}`
    throw new ApiError(message, response.status, data)
  }

  return data as T
}

/**
 * Optional opt-out flags on a fetch call.
 * - `suppressPermissionToast`: skip the auto-raised 403 toast (for features that
 *   intentionally probe for permission, e.g., the merge wizard).
 */
type FetchOptionsExtra = {
  suppressPermissionToast?: boolean
}

/**
 * Authenticated fetch - adds Authorization header.
 *
 * Per RBAC Module spec #10 §3.6:
 * - 401 → log the user out (token expired / invalid)
 * - 403 → raise a friendly toast via ui-store (deduped 5s on `{url, errorCode, required_permission}`)
 *         OR force-logout when `errorCode === 'USER_DEACTIVATED'`
 *
 * To opt out of the 403 toast (e.g., for probe calls): pass
 * `options.suppressPermissionToast = true`.
 */
export async function authFetch<T>(
  endpoint: string,
  token: string,
  options: RequestInit & FetchOptionsExtra = {}
): Promise<T> {
  const { suppressPermissionToast, ...fetchOptions } = options
  try {
    return await apiFetch<T>(endpoint, {
      ...fetchOptions,
      headers: {
        ...fetchOptions.headers,
        Authorization: `Bearer ${token}`,
      },
    })
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 401) {
        // Access token expired/invalid. Try a SILENT refresh and retry once
        // before disturbing the user. Only if refresh is impossible do we
        // surface expiry (in-place modal mid-session, or logout on fresh load).
        const { useAuthStore } = await import('@/stores/auth-store')
        const store = useAuthStore.getState()
        // Already handling expiry — don't spawn more refreshes or retries.
        if (store.sessionExpired) throw error

        const newToken = await store.refreshSession()
        if (newToken) {
          // Retry ONCE with the fresh token. Use apiFetch (not authFetch) so a
          // second 401 surfaces to the caller instead of recursing.
          return await apiFetch<T>(endpoint, {
            ...fetchOptions,
            headers: {
              ...fetchOptions.headers,
              Authorization: `Bearer ${newToken}`,
            },
          })
        }
        store.handleAuthExpiry()
      } else if (error.status === 403) {
        await handle403Response(endpoint, error, suppressPermissionToast)
      }
    }
    throw error
  }
}

/**
 * Centralized handler for HTTP 403 responses.
 *
 * Reads the RBAC v1 standard body shape (per spec #7 §6.5):
 *   { success, message, errorCode, details: { required_permission, ... } }
 *
 * Routes on errorCode:
 * - USER_DEACTIVATED → force logout (account no longer valid)
 * - TOKEN_EXPIRED    → force logout (caller should also redirect to login)
 * - PERMISSION_DENIED, SCOPE_MISMATCH (default) → toast
 *
 * On a legacy 403 without `errorCode`, falls back to a generic toast.
 */
async function handle403Response(
  endpoint: string,
  error: ApiError,
  suppressPermissionToast?: boolean
) {
  const errorCode = (error.data as { errorCode?: string } | null)?.errorCode ?? null
  const details = (error.data as { details?: { required_permission?: string | null } } | null)?.details
  const requiredPermission = details?.required_permission ?? null

  if (errorCode === 'USER_DEACTIVATED' || errorCode === 'TOKEN_EXPIRED') {
    const { useAuthStore } = await import('@/stores/auth-store')
    useAuthStore.getState().logout()
    return
  }

  if (!suppressPermissionToast) {
    const { useUIStore } = await import('@/stores/ui-store')
    useUIStore.getState().showPermissionDenied({
      url: endpoint,
      message: error.message,
      requiredPermission,
      errorCode
    })
  }
}

/**
 * Authenticated fetch for blob/file downloads (PDF, Excel, etc.)
 * Use this instead of raw fetch() for file exports to ensure correct base URL.
 *
 * @param endpoint - API endpoint (e.g., '/api/v1/vouchers/123/pdf')
 * @param token - JWT access token
 * @param options - Optional fetch options (method defaults to GET)
 * @returns Promise<Blob> - The file blob
 * @throws ApiError on failure
 *
 * @example
 * const blob = await authFetchBlob('/api/v1/reports/trial-balance/pdf?fromDate=2024-01-01', token)
 * triggerBlobDownload(blob, 'trial-balance.pdf')
 */
export async function authFetchBlob(
  endpoint: string,
  token: string,
  options: RequestInit = {}
): Promise<Blob> {
  const url = `${API_BASE_URL}${endpoint}`

  const tunnelHeaders = {
    ...(API_BASE_URL.includes('ngrok') && { 'ngrok-skip-browser-warning': 'true' }),
    ...(API_BASE_URL.includes('zrok') && { 'skip_zrok_interstitial': 'TRUE' }),
  }
  const doFetch = (bearer: string) =>
    fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${bearer}`,
        ...tunnelHeaders,
        ...options.headers,
      },
    })

  let response = await doFetch(token)

  // On 401, attempt a silent refresh and retry once before surfacing expiry.
  if (response.status === 401) {
    const { useAuthStore } = await import('@/stores/auth-store')
    const store = useAuthStore.getState()
    if (!store.sessionExpired) {
      const newToken = await store.refreshSession()
      if (newToken) {
        response = await doFetch(newToken)
      } else {
        store.handleAuthExpiry()
      }
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.message || `Download failed with status ${response.status}`
    throw new ApiError(message, response.status, errorData)
  }

  return response.blob()
}

/**
 * Trigger a browser download for a blob
 * Creates a temporary link and clicks it to start the download.
 *
 * @param blob - The file blob to download
 * @param filename - The filename for the downloaded file
 *
 * @example
 * const blob = await authFetchBlob('/api/v1/vouchers/123/pdf', token)
 * triggerBlobDownload(blob, 'voucher-PV-123.pdf')
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
